import { useRef, useState, useEffect } from 'react';
import { Play, CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';

export default function WatchStep({ videoUrl, requiredPercent, initialPercent, onProgress, onContinue }: {
  videoUrl: string;
  requiredPercent: number;
  initialPercent: number;
  onProgress: (pct: number) => void;
  onContinue: () => void;
}) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [watchedPercent, setWatchedPercent] = useState(initialPercent);
  const required = requiredPercent > 0 ? requiredPercent : 80;
  const met = watchedPercent >= required;

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    const handler = () => {
      if (v.duration > 0) {
        const pct = Math.round((v.currentTime / v.duration) * 100);
        setWatchedPercent(prev => {
          const next = Math.max(prev, pct);
          if (next > prev) onProgress(next);
          return next;
        });
      }
    };
    v.addEventListener('timeupdate', handler);
    return () => v.removeEventListener('timeupdate', handler);
  }, [onProgress]);

  if (!videoUrl) {
    return (
      <div className="space-y-4">
        <div className="aspect-video bg-muted rounded-xl flex items-center justify-center border border-dashed border-border">
          <div className="text-center px-4">
            <Play className="w-10 h-10 text-muted-foreground mx-auto mb-2" />
            <p className="text-sm font-medium text-muted-foreground">Video not yet available</p>
            <p className="text-xs text-muted-foreground mt-1">The training video will appear here once published</p>
          </div>
        </div>
        <Button onClick={onContinue} className="w-full h-12 text-base">Continue Without Video</Button>
      </div>
    );
  }

  const isYouTube = videoUrl.includes('youtube.com') || videoUrl.includes('youtu.be');

  return (
    <div className="space-y-4">
      <h2 className="text-lg font-bold text-foreground">Watch the Training Video</h2>
      <p className="text-sm text-muted-foreground">Watch at least {required}% of the video to proceed.</p>

      {isYouTube ? (
        <div className="aspect-video rounded-xl overflow-hidden border border-border">
          <iframe
            src={`https://www.youtube.com/embed/${videoUrl.includes('youtu.be') ? videoUrl.split('/').pop() : new URLSearchParams(new URL(videoUrl).search).get('v')}`}
            className="w-full h-full" allowFullScreen />
        </div>
      ) : (
        <video ref={videoRef} src={videoUrl} controls playsInline
          className="w-full rounded-xl aspect-video bg-muted border border-border" preload="metadata" />
      )}

      <div className="flex items-center gap-2">
        <div className="flex-1 h-2.5 bg-muted rounded-full overflow-hidden">
          <div className="h-2.5 bg-primary rounded-full transition-all" style={{ width: `${Math.min(watchedPercent, 100)}%` }} />
        </div>
        <span className={`text-xs font-bold ${met ? 'text-primary' : 'text-muted-foreground'}`}>
          {watchedPercent}%{met ? ' ✓' : ` / ${required}%`}
        </span>
      </div>

      {met && (
        <div className="bg-primary/5 border border-primary/20 rounded-xl p-3 flex items-center gap-2">
          <CheckCircle className="w-5 h-5 text-primary shrink-0" />
          <p className="text-sm text-foreground font-medium">Video requirement met!</p>
        </div>
      )}

      <Button onClick={onContinue} disabled={!met && !isYouTube} className="w-full h-12 text-base">
        {met || isYouTube ? 'Continue' : `Watch to ${required}%`}
      </Button>
    </div>
  );
}
